import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatInput } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  forkJoin,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { VisitorPlaceService } from '../../../../event-calendar/services/visitor.place.service';
import { VisitorPlace } from '../../../../shared/models/visitor.place';
import { AsyncPipe, CommonModule, NgForOf } from '@angular/common';
import { SharedModule } from '../../../../shared/shared.module';
import { ItineraryAttraction } from '../../../models/itinerary';
import { TripAttraction } from '../../../../shared/models/vititor.trip.storage.command';
import { VisitorEventService } from '../../../../event-calendar/services/visitor.event.service';
import { VisitorEvent } from '../../../../shared/models/visitor.event';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PendoService } from 'src/app/modules/shared/services/pendo.service';

@Component({
  selector: 'ig-search-attractions',
  standalone: true,
  imports: [
    MatInput,
    ReactiveFormsModule,
    AsyncPipe,
    NgForOf,
    CommonModule,
    SharedModule,
  ],
  templateUrl: './search.attractions.component.html',
  styleUrl: './search.attractions.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SearchAttractionsComponent {
  searchControl: FormControl = new FormControl('');
  items: VisitorPlace[] | VisitorEvent[] = [];
  isLoading = false;
  isHide = true;
  isShowAdded: {
    places: boolean[];
    events: boolean[];
  } = { places: [], events: [] };

  @Input() style: 'dark' | 'light' = 'light';
  @Input() limit = 20;
  @Input() hubName = '';
  @Input() searchFor: 'places' | 'events' | 'all' = 'places';
  @Input() existing: ItineraryAttraction[] | TripAttraction[] = [];
  @Output() addPlace: EventEmitter<VisitorPlace | VisitorEvent> =
    new EventEmitter();

  snackBar = inject(MatSnackBar);
  pendoService = inject(PendoService);

  templateData1 = {
    title: 'Template 1 Title',
    content: 'This is the content of template 1.',
  };

  templateData2 = {
    title: 'Template 2 Title',
    content: 'This is the content of template 2.  It has different data.',
  };

  constructor(
    public placeService: VisitorPlaceService,
    public eventService: VisitorEventService
  ) {
    this.searchControl.valueChanges
      .pipe(
        tap((search) => {
          if (search.trim()) {
            this.isHide = true;
          }
        }),
        debounceTime(1600),
        distinctUntilChanged(),
        tap((search) => {
          if (search.trim()) {
            this.isLoading = true;
            this.isHide = false;
          } else {
            this.isLoading = false;
          }
        }),
        switchMap((search) => {
          if (!search.trim()) {
            this.isHide = true;
            return of([]);
          }

          const getPlaces$ = this.placeService.getPlaces(this.hubName, {
            isForSearchView: true,
            limit: 12,
            search
          });

          const getEvents$ = this.eventService.getEvents(this.hubName, {
            isForSearchView: true,
            limit: 12,
            search
          });

          return this.searchFor === 'all'
            ? forkJoin([getPlaces$, getEvents$])
            : this.searchFor === 'events'
              ? getEvents$
              : getPlaces$;
        }),
        delay(200),
        tap((value) => {
          console.log('value', value);
          this.isLoading = false;
          this.isShowAdded = {
            places: [],
            events: [],
          };
          if (this.searchFor === 'all') {
            // value = [...value[0], ...value[1]];
            value = [
              ...(Array.isArray(value[0]) ? value[0] : []),
              ...(Array.isArray(value[1]) ? value[1] : []),
            ];
          }

          for (const place of value) {
            const isExist = this.existing.any((x) => x.feedId === place._id);
            if (this.isEvent(place)) {
              this.isShowAdded.events.push(isExist);
            } else {
              this.isShowAdded.places.push(isExist);
            }
            // if (this.existing.find((x) => x.feedId === place._id.toString())) {
            //   if (this.isEvent(place)) {
            //     this.isShowAdded.events.push(true);
            //   } else {
            //     this.isShowAdded.places.push(true);
            //   }
            // } else {
            //   this.isShowAdded.push(false);
            // }
          }
        })
      )
      .subscribe((value) => {
        if (this.searchFor === 'all') {
          // value = [...value[0], ...value[1]];
          value = [
            ...(Array.isArray(value[0]) ? value[0] : []),
            ...(Array.isArray(value[1]) ? value[1] : []),
          ];
        }
        this.items = value;
        this.pendoService.track('Search Events and Places to Add');
      });
  }

  get placeholder() {
    return `Search ${this.searchFor === 'all' ? 'events and places' : this.searchFor
      } to add`;
  }

  getPlaces() {
    return this.items.filter((x) => !x.startTime);
  }

  getEvents() {
    return this.items.filter((x) => x.startTime);
  }

  add(place: VisitorPlace | VisitorEvent, index: number) {
    if (this.existing.length >= this.limit) {
      this.snackBar.open(
        'You have reached the maximum number of attractions',
        'Close',
        {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: 'ig-error-snackbar',
        }
      );
      return;
    }
    this.addPlace.emit(place);
    if (this.isEvent(place)) {
      this.isShowAdded.events[index] = true;
    } else {
      this.isShowAdded.places[index] = true;
    }
  }

  onInputFocus($event) {
    if (this.items.length) {
      setTimeout(() => {
        this.isHide = false;
      }, 300);
    }
  }

  iconClick($event) {
    if (this.searchControl.value) {
      this.searchControl.setValue('');
      this.items = [];
    }
  }

  isEvent(item: VisitorPlace | VisitorEvent) {
    return item['startTime'];
  }

  isItemAdded(item: VisitorPlace | VisitorEvent, index: number) {
    if (this.isEvent(item)) {
      return this.isShowAdded.events[index];
    } else {
      return this.isShowAdded.places[index];
    }
  }
}
